import { DateTime } from 'luxon'

import { type DayOfWeek } from '@ntc/types/hcl'

import { OPEN_HOUR_TYPE, STORE_OPEN_CLOSE_STATUS } from '@constants/constants'

export type RegularStoreHours = {
  type: typeof OPEN_HOUR_TYPE.regular | typeof OPEN_HOUR_TYPE.closed
  dayOfWeek: DayOfWeek
  open: string
  close: string
}

export type SpecialStoreHours = {
  type: typeof OPEN_HOUR_TYPE.holiday | typeof OPEN_HOUR_TYPE.special
  date: string
  open: string
  close: string
}

export type StoreHour = RegularStoreHours | SpecialStoreHours

export const isRegularHours = (storeHour: StoreHour): storeHour is RegularStoreHours => {
  return storeHour.type === OPEN_HOUR_TYPE.regular || storeHour.type === OPEN_HOUR_TYPE.closed
}

export const isSpecialHours = (storeHour: StoreHour): storeHour is SpecialStoreHours => {
  return storeHour.type === OPEN_HOUR_TYPE.special || storeHour.type === OPEN_HOUR_TYPE.holiday
}

export const getStoreHoursForDate = ({
  date,
  storeHours,
  timeZone
}: {
  date: DateTime
  storeHours: Array<StoreHour>
  timeZone?: string
}) => {
  const dayOfWeek = date.toFormat('cccc')
  const dateString = date.toFormat('yyyy-MM-dd')

  const selectedDateStoreHours = storeHours
    // need to sort so that holiday and special hours trump regular hours
    .sort((a) => (a.type === OPEN_HOUR_TYPE.regular ? 1 : -1))
    .find((hourDetails) =>
      // special dates and holidays are only for 1 specific dates
      isRegularHours(hourDetails)
        ? hourDetails.dayOfWeek.toUpperCase() === dayOfWeek.toUpperCase()
        : hourDetails.date === dateString
    )

  const rawOpenDate = DateTime.fromISO(`${dateString}T${selectedDateStoreHours?.open}`, { zone: timeZone })
  const rawCloseDate = DateTime.fromISO(`${dateString}T${selectedDateStoreHours?.close}`, { zone: timeZone })

  return {
    type: selectedDateStoreHours?.type,
    openDate: rawOpenDate.isValid ? rawOpenDate : null,
    openTime: rawOpenDate.isValid ? rawOpenDate.toMillis() : 0,
    closeDate: rawCloseDate.isValid ? rawCloseDate : null,
    closeTime: rawCloseDate.isValid ? rawCloseDate.toMillis() : Infinity
  }
}

export const getStoreOpenCloseStatusForDate = ({
  date,
  timeZone,
  storeHours
}: {
  date: DateTime
  timeZone?: string
  storeHours: Array<StoreHour>
}) => {
  const selectedDateTime = date.toMillis()

  const selectedDateHours = getStoreHoursForDate({ date, storeHours, timeZone })

  const isStoreOpen =
    selectedDateHours.type === OPEN_HOUR_TYPE.holiday
      ? false
      : selectedDateTime >= selectedDateHours.openTime && selectedDateTime <= selectedDateHours.closeTime

  const nextDayHours =
    selectedDateTime < selectedDateHours.openTime
      ? selectedDateHours
      : getStoreHoursForDate({ date: date.plus({ days: 1 }), storeHours, timeZone })

  return {
    isStoreOpen,
    storeStatus: isStoreOpen
      ? STORE_OPEN_CLOSE_STATUS.OPEN_NOW
      : selectedDateHours.type === OPEN_HOUR_TYPE.holiday
        ? STORE_OPEN_CLOSE_STATUS.CLOSED_TODAY
        : STORE_OPEN_CLOSE_STATUS.CLOSED_NOW,
    closingDateTime:
      // Only show if store is currently open
      isStoreOpen && selectedDateHours.closeDate ? selectedDateHours.closeDate : null,
    nextOpenDateTime:
      // Only show if store is currently closed
      // Do not show if next day is a holiday
      !isStoreOpen && nextDayHours.type != OPEN_HOUR_TYPE.holiday && nextDayHours.openDate
        ? nextDayHours.openDate
        : null
  }
}

export const getStoreSpecialHoursForDate = ({
  date,
  storeHours,
  timeZone
}: {
  date: DateTime
  storeHours: Array<StoreHour>
  timeZone?: string
}) => {
  return storeHours
    .filter(isSpecialHours)
    .map((specialHours) => {
      const dateTime = DateTime.fromISO(specialHours.date, { zone: timeZone })
      const openDateTime = DateTime.fromISO(specialHours.open, { zone: timeZone })
      const closeDateTime = DateTime.fromISO(specialHours.close, { zone: timeZone })

      return {
        ...specialHours,
        dateTime,
        openDateTime,
        closeDateTime,
        dateInMillis: date.toMillis()
      }
    })
    .filter((specialHours) => {
      const dateDiffInDays = specialHours.dateTime.diff(date, 'days').days

      return (
        // validate that the date, open, and close dates are all valid ISO
        specialHours.dateTime.isValid &&
        specialHours.openDateTime.isValid &&
        specialHours.closeDateTime.isValid &&
        // only show when the special date is today and within the next 14 days
        // add +/- 1 day to input date as buffer
        dateDiffInDays >= -1 &&
        dateDiffInDays <= 14 + 1
      )
    })
    .sort((a, b) => a.dateInMillis - b.dateInMillis)
}
